/** Helpers de formatação. Sem dependência de tokens visuais. */

export function fmtDateTime(iso?: string | null): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return iso;
  return d.toLocaleString("pt-BR", {
    timeZone: "America/Sao_Paulo",
    day: "2-digit",
    month: "2-digit",
    year: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
}

export function fmtTime(iso?: string | null): string {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "";
  return d.toLocaleTimeString("pt-BR", {
    timeZone: "America/Sao_Paulo",
    hour: "2-digit",
    minute: "2-digit",
  });
}

export function fmtSaoPauloDateInput(date = new Date()): string {
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: "America/Sao_Paulo",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).formatToParts(date);
  const values = Object.fromEntries(parts.map((part) => [part.type, part.value]));
  return `${values.year}-${values.month}-${values.day}`;
}

export function addSaoPauloCalendarDays(dateInput: string, days: number): string {
  const base = new Date(`${dateInput}T12:00:00-03:00`);
  if (isNaN(base.getTime())) return dateInput;
  base.setUTCDate(base.getUTCDate() + days);
  return fmtSaoPauloDateInput(base);
}

export function fmtCurrencyBRL(n: number | null | undefined): string | null {
  if (n == null) return null;
  const num = Number(n);
  if (!Number.isFinite(num)) return null;
  return num.toLocaleString("pt-BR", {
    style: "currency",
    currency: "BRL",
    maximumFractionDigits: 0,
  });
}

const PT_VALUE_LABELS: Record<string, string> = {
  // Intent
  purchase: "Compra",
  rental_long: "Locação de longa duração",
  rental_short: "Temporada",
  listing: "Captação",
  partnership: "Parceria",
  info: "Informação",
  compra: "Compra",
  locacao: "Locação",
  temporada: "Temporada",
  captacao: "Captação",
  informacao: "Informação",

  // Listing service
  sale: "Venda",
  appraisal: "Avaliação",
  management: "Gestão",

  // Property type
  apartment: "Apartamento",
  house: "Casa",
  penthouse: "Cobertura",
  townhouse: "Sobrado",
  land: "Terreno",
  commercial: "Comercial",
  warehouse: "Galpão",
  gated_house: "Casa em condomínio",
  island: "Ilha",
  office: "Sala comercial",

  // Group composition
  family: "Família",
  couple: "Casal",
  friends: "Amigos",
  colleagues: "Colegas",
  mixed_group: "Grupo misto",

  // Requirement tags
  near_beach: "Perto da praia",
  near_metro: "Perto do metrô",
  ocean_view: "Vista para o mar",
  mountain_view: "Vista para a montanha",
  pool: "Piscina",
  gym: "Academia",
  jacuzzi: "Jacuzzi",
  sauna: "Sauna",
  concierge: "Concierge",
  doorman: "Portaria",
  elevator: "Elevador",
  gated_community: "Condomínio fechado",
  parking: "Vaga de garagem",
  balcony: "Varanda",
  terrace: "Terraço",
  garden: "Jardim",
  equipped_kitchen: "Cozinha equipada",
  pet_friendly: "Aceita pets",
  furnished: "Mobiliado",
  high_floor: "Andar alto",
};

export function fmtCanonicalPt(value?: string | number | boolean | null): string | null {
  if (value == null || value === "") return null;
  const raw = String(value).trim();
  if (!raw) return null;
  return PT_VALUE_LABELS[raw] || raw;
}

export function fmtCanonicalPtItems(items?: string[] | string | null): string[] | string | null {
  if (Array.isArray(items)) return items.map((item) => fmtCanonicalPt(item) || item);
  return fmtCanonicalPt(items);
}

export function fmtDisplayName(value?: string | null, fallback = "Contato sem nome"): string {
  const raw = String(value ?? "").trim();
  if (!raw) return fallback;
  const normalized = raw.toLowerCase();
  if (normalized === "null" || normalized === "undefined") return fallback;
  return raw;
}

export function fmtPhoneDigits(p?: string | null): string {
  return String(p || "").replace(/\D/g, "");
}

/**
 * Formata telefone E.164 ou nacional pra display:
 *   +5521988887777    → "+55 (21) 98888-7777"
 *   552133334444      → "+55 (21) 3333-4444"
 *   21988887777       → "(21) 98888-7777"
 *
 * Fallback: retorna o input cru.
 */
export function fmtPhoneDisplay(phone?: string | null): string {
  if (!phone) return "";
  const raw = String(phone).trim();
  const digits = raw.replace(/\D/g, "");

  // +55 móvel (13 dígitos): 55 + DD + 9XXXX-XXXX
  if (digits.length === 13 && digits.startsWith("55")) {
    const dd = digits.slice(2, 4);
    const a = digits.slice(4, 9);
    const b = digits.slice(9, 13);
    return `+55 (${dd}) ${a}-${b}`;
  }
  // +55 fixo (12 dígitos): 55 + DD + XXXX-XXXX
  if (digits.length === 12 && digits.startsWith("55")) {
    const dd = digits.slice(2, 4);
    const a = digits.slice(4, 8);
    const b = digits.slice(8, 12);
    return `+55 (${dd}) ${a}-${b}`;
  }
  // Nacional móvel (11 dígitos): DD + 9XXXX-XXXX
  if (digits.length === 11) {
    const dd = digits.slice(0, 2);
    const a = digits.slice(2, 7);
    const b = digits.slice(7, 11);
    return `(${dd}) ${a}-${b}`;
  }
  // Nacional fixo (10 dígitos): DD + XXXX-XXXX
  if (digits.length === 10) {
    const dd = digits.slice(0, 2);
    const a = digits.slice(2, 6);
    const b = digits.slice(6, 10);
    return `(${dd}) ${a}-${b}`;
  }
  return raw;
}

export function escapeHtml(s: unknown): string {
  return String(s == null ? "" : s)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}
